import React, { useState, useEffect } from "react";
// Assuming jQuery and jQuery UI are installed: npm install jquery jquery-ui
import $ from 'jquery'; 
import 'jquery-ui/themes/base/all.css'; 
import 'jquery-ui/ui/widgets/datepicker';

import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { FiPlus, FiTrash2, FiFileText, FiUser, FiPackage, FiTruck, FiList, FiCheckCircle, FiXCircle } from "react-icons/fi"; // Added more relevant icons
import { FaIndianRupeeSign } from "react-icons/fa6";

const GST_SLABS = [0, 5, 12, 18, 28];
const PO_STATUSES = ["Draft", "Issued", "Partially Received", "Closed"];

const splitGST = (totalTax, gstType) => {
    if (gstType === "IGST") return { IGST: totalTax, CGST: 0, SGST: 0 };
    return { IGST: 0, CGST: totalTax / 2, SGST: totalTax / 2 };
};


function CreatePurchaseOrder() {
    // Helper function to get default date in YYYY-MM-DD format
    const getDefaultDate = (daysOffset = 0) => {
        const date = new Date();
        date.setDate(date.getDate() + daysOffset);
        return date.toISOString().split("T")[0];
    };
    
    // --- STATE CHANGES FOR PURCHASE ORDER ---
    const [purchaseOrder, setPurchaseOrder] = useState({ 
        poNumber: "PO-001", // Changed key name
        poDate: getDefaultDate(0), // Changed key name
        deliveryDate: getDefaultDate(30), // Changed key name from validityDate
        status: "Draft", // Updated default status
        
        // REVERSED ROLES for PO: "Customer" is now "Vendor", "Seller" is now "Buyer"
        vendor: { name: "Acme Corp", email: "contact@acme.com", phone: "9876543210", address: "123 Main St, State Name", gstin: "27AABCA1234A1Z5", state: "Maharashtra" }, // Supplier Details
        buyer: { name: "Your Company Name", gstin: "27AABCA5678A1Z5", state: "Maharashtra", phone: "9000000000" }, // Your Company Details

        items: [
            { description: "Raw Material X", hsn: "1234", qty: 100, rate: 5, tax: 18 },
            { description: "Packaging Supplies", hsn: "5678", qty: 50, rate: 20, tax: 12 }
        ],
        discount: 0, 
        additionalCharges: 0, 
        gstInclusive: false, 
        notes: "Delivery must be completed within 30 days of the PO date.", // Updated notes
    });

    const [applyGSTItemWise, setApplyGSTItemWise] = useState(true);
    const [poGSTPercent, setPoGSTPercent] = useState(18); // Changed variable name

    // Update function for general PO metadata
    const updatePOMeta = (fieldName, value) => {
        setPurchaseOrder(prev => ({ ...prev, [fieldName]: value }));
    };

    // Handler for PO Status
    const handlePOStatusChange = (status) => {
        setPurchaseOrder(prev => ({ ...prev, status: status }));
    };
    
    // ** --- JQUERY DATEPICKER INTEGRATION (Updated for PO) --- **
    useEffect(() => {
        const $poDate = $('#poDateInput');
        const $deliveryDate = $('#deliveryDateInput');

        const handleDateSelect = (dateText, inst) => {
            const fieldName = inst.id === 'poDateInput' ? 'poDate' : 'deliveryDate';
            updatePOMeta(fieldName, dateText);
        };

        // Initialize datepickers
        $poDate.datepicker({
            dateFormat: "yy-mm-dd",
            onSelect: handleDateSelect,
            defaultDate: purchaseOrder.poDate 
        });

        $deliveryDate.datepicker({
            dateFormat: "yy-mm-dd",
            onSelect: handleDateSelect,
            defaultDate: purchaseOrder.deliveryDate
        });

        return () => {
            $poDate.datepicker('destroy');
            $deliveryDate.datepicker('destroy');
        };
    }, []);

    // Update the Datepicker UI when React state changes
    useEffect(() => {
        $('#poDateInput').datepicker('setDate', purchaseOrder.poDate);
        $('#deliveryDateInput').datepicker('setDate', purchaseOrder.deliveryDate);
    }, [purchaseOrder.poDate, purchaseOrder.deliveryDate]);
    // ** --- END JQUERY DATEPICKER INTEGRATION --- **

    // --- Item Management Functions (Updated to use purchaseOrder state) ---
    const addItem = () =>
        setPurchaseOrder((prev) => ({
            ...prev,
            items: [...prev.items, { description: "", hsn: "", qty: 1, rate: 0, tax: 0 }],
        }));

    const removeItem = (index) =>
        setPurchaseOrder((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));

    const updateItem = (index, field, value) => {
        const items = [...purchaseOrder.items];
        items[index][field] = value;
        setPurchaseOrder({ ...purchaseOrder, items });
    };

    // --- Buyer/Vendor Update Functions (Renamed/Updated) ---
    const updateVendor = (field, value) => { // Was updateCustomer
        setPurchaseOrder(prev => ({ 
            ...prev, 
            vendor: { ...prev.vendor, [field]: value } 
        }));
    };

    const updateBuyer = (field, value) => { // Was updateSeller
        setPurchaseOrder(prev => ({ 
            ...prev, 
            buyer: { ...prev.buyer, [field]: value } 
        }));
    };

    // --- Calculations --- (Updated to use purchaseOrder state and poGSTPercent)
    const { items, discount, additionalCharges, gstInclusive, vendor, buyer } = purchaseOrder;
    const itemSubtotal = items.reduce((acc, item) => acc + item.qty * item.rate, 0);
    let subtotal = itemSubtotal;
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount + Number(additionalCharges);

    const calculateTotalTax = () => {
        const taxRateToUse = applyGSTItemWise ? 0 : poGSTPercent; // Used poGSTPercent

        if (applyGSTItemWise) {
            return items.reduce((acc, item) => {
                const itemAmount = item.qty * item.rate;
                const taxRate = item.tax;
                
                if (gstInclusive) {
                    return acc + (itemAmount * taxRate) / (100 + taxRate);
                } else {
                    return acc + (itemAmount * taxRate) / 100;
                }
            }, 0);
        } else {
            if (gstInclusive) {
                return (taxableAmount * taxRateToUse) / (100 + taxRateToUse);
            } else {
                return (taxableAmount * taxRateToUse) / 100;
            }
        }
    }

    const totalTax = calculateTotalTax();

    // The logic for IGST/CGST_SGST depends on the state of the BUYER (Your Co.) and the VENDOR (Supplier)
    const isInterState = buyer.state.trim().toLowerCase() !== vendor.state.trim().toLowerCase();
    const gstType = isInterState ? "IGST" : "CGST_SGST";
    const { IGST, CGST, SGST } = splitGST(totalTax, gstType);

    const grandTotal = gstInclusive ? taxableAmount : taxableAmount + totalTax;

    const getItemAmountDisplay = (item, isItemWise) => {
        const taxRate = isItemWise ? item.tax : poGSTPercent;
        const grossAmount = item.qty * item.rate;

        if (gstInclusive) {
            return grossAmount.toFixed(2); 
        } else {
            // Amount = (Gross Amount) + Tax on Gross Amount
            const totalAmount = grossAmount + (grossAmount * taxRate) / 100; 
            return totalAmount.toFixed(2); 
        }
    }
    // --- END Calculations ---

    // ** --- START FORM SUBMISSION (Updated for PO) --- **
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const dataToSend = {
            ...purchaseOrder,
            discount: Number(purchaseOrder.discount),
            additionalCharges: Number(purchaseOrder.additionalCharges),
            itemSubtotal: itemSubtotal,
            taxableAmount: taxableAmount,
            totalTax: totalTax,
            grandTotal: grandTotal,
            gstBreakdown: { IGST, CGST, SGST, gstType, isInterState }
        };

        console.log("Purchase Order data to send to backend:", dataToSend);
        
        // Actual fetch logic here...
        
        alert("Purchase Order data prepared for submission. Check console for payload.");
    };
    // ** --- END FORM SUBMISSION --- **


    // ** --- UI Components for Status Toggles (Updated for PO) --- **

    const renderStatusButton = (status) => {
        const isActive = purchaseOrder.status === status;
        let colorClass = 'secondary';
        if (status === 'Draft') colorClass = 'info';
        if (status === 'Issued') colorClass = 'success';
        if (status === 'Closed') colorClass = 'dark';
        
        let btnClass = isActive ? `btn-${colorClass} text-white` : `btn-outline-${colorClass}`;
        
        // Keep Issued and Closed as final states once set
        const isDisabled = isActive && (status === 'Issued' || status === 'Closed'); 

        return (
            <button
                type="button"
                className={`btn ${btnClass}`}
                onClick={() => handlePOStatusChange(status)}
                disabled={isDisabled} 
            >
                {status}
            </button>
        );
    };

    const POStatusToggle = () => (
        <div className="col-md-3">
            <label className="form-label">PO Status</label>
            <div className="btn-group btn-group-sm w-100" role="group" aria-label="PO Status selection">
                {renderStatusButton('Draft')}
                {renderStatusButton('Issued')}
                {renderStatusButton('Closed')}
            </div>
        </div>
    );
    
    // ** --- END UI Components --- **


    return (
        <>
            <Topbar />
            <Sidebar />
            <main className="main-wrapper">
                <form onSubmit={handleSubmit}>
                    <div className="main-content p-1">

                        <div className="mb-3">
                            <h2 className="mb-2 d-flex align-items-center">
                            <FiPackage className="me-2 text-primary" /> Create New GST **Purchase Order**
                        </h2>
                            <small className="text-muted ">Generate and manage purchase orders to vendors/suppliers.</small>
                        </div>
                        
                        <h6 className="card-title p-2 mb-2 d-flex align-items-center"> Purchase Order Details</h6>

                        {/* Top Info & Settings Card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-body">
                                
                                <div className="row g-2">
                                    {/* PO Metadata */}
                                    <div className="col-md-12 mt-0">
                                        <div className="row g-2">
                                            {/* PO Number */}
                                            <div className="col-md-3"> 
                                                <label className="form-label ">PO Number</label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={purchaseOrder.poNumber} 
                                                    onChange={(e) => updatePOMeta("poNumber", e.target.value)} 
                                                />
                                            </div>

                                            {/* PO Date */}
                                            <div className="col-md-3">
                                                <label className="form-label ">PO Date</label>
                                                <input
                                                    type="text"
                                                    id="poDateInput" // Changed ID
                                                    className="form-control form-control-sm"
                                                    value={purchaseOrder.poDate} 
                                                    onChange={(e) => updatePOMeta("poDate", e.target.value)} 
                                                />
                                            </div>
                                            
                                            {/* Delivery Date */}
                                            <div className="col-md-3">
                                                <label className="form-label ">Expected Delivery Date</label>
                                                <input
                                                    type="text"
                                                    id="deliveryDateInput" // Changed ID
                                                    className="form-control form-control-sm"
                                                    value={purchaseOrder.deliveryDate} 
                                                    onChange={(e) => updatePOMeta("deliveryDate", e.target.value)} 
                                                />
                                            </div>
                                            
                                            {/* PO Status (UPDATED) */}
                                            <POStatusToggle />
                                            
                                        </div>
                                    </div>

                                    {/* GST Mode and Application */}
                                    <div className="col-md-12 tax-settings-container">
                                        <hr className="my-3"/>
                                        <h6>GST & Tax Settings</h6>
                                        <div className="row g-2">
                                            {/* 1. GST Mode Toggle (Same logic) */}
                                            <div className="col-md-4">
                                                <label className="form-label">Price Mode</label>
                                                <div className="btn-group btn-group-sm w-100" role="group" aria-label="GST Mode selection">
                                                    <button
                                                        type="button"
                                                        className={`btn ${purchaseOrder.gstInclusive ? 'btn-outline-primary' : 'btn-primary'}`}
                                                        onClick={() => setPurchaseOrder({ ...purchaseOrder, gstInclusive: false })}
                                                    >
                                                        Tax Exclusive
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn ${purchaseOrder.gstInclusive ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => setPurchaseOrder({ ...purchaseOrder, gstInclusive: true })}
                                                    >
                                                        Tax Inclusive
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 2. Apply Tax By Toggle (Same logic) */}
                                            <div className="col-md-4">
                                                <label className="form-label">Apply Tax By</label>
                                                <div className="btn-group btn-group-sm w-100" role="group" aria-label="Apply Tax By selection">
                                                    <button
                                                        type="button"
                                                        className={`btn ${applyGSTItemWise ? 'btn-primary' : 'btn-outline-primary'}`}
                                                        onClick={() => setApplyGSTItemWise(true)}
                                                    >
                                                        Item-wise Rate
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn ${applyGSTItemWise ? 'btn-outline-primary' : 'btn-primary'}`}
                                                        onClick={() => setApplyGSTItemWise(false)}
                                                    >
                                                        PO-wise Rate
                                                    </button>
                                                </div>
                                            </div>

                                            {/* 3. PO GST % Select (Updated variable name) */}
                                            {!applyGSTItemWise && (
                                                <div className="col-md-4">
                                                    <label className="form-label">PO GST %</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={poGSTPercent}
                                                        onChange={(e) => setPoGSTPercent(parseFloat(e.target.value))}
                                                    >
                                                        {GST_SLABS.map((g) => (
                                                            <option key={g} value={g}>
                                                                {g}%
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    
                        
                        {/* Billing Details (Buyer/Vendor Info) - REVERSED ORDER */}
                        <div className="row g-4 mb-4">
                            <div className="col-md-12"> 
                                <h6 className="card-title p-2 mb-2 border-bottom d-flex align-items-center"> Parties Involved</h6>

                                <div className="card shadow-sm mb-4">
                                    <div className="card-body">
                                        <div className="row">
                                            {/* BUYER Details (Your Company) */}
                                            <div className="col-md-6 border-end">
                                                <h6 className="d-flex align-items-center"><FiUser className="me-2 text-primary"/> Buyer Details (**Your Company**)</h6>
                                                <div className="row g-2">
                                                    <div className="col-12">
                                                        <label className="form-label ">Company Name</label>
                                                        <input type="text" className="form-control form-control-sm" value={buyer.name} onChange={(e) => updateBuyer("name", e.target.value)} />
                                                    </div>
                                                    <div className="col-12"> 
                                                        <label className="form-label ">Phone Number</label>
                                                        <input type="text" className="form-control form-control-sm" value={buyer.phone} onChange={(e) => updateBuyer("phone", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label ">GSTIN</label>
                                                        <input type="text" className="form-control form-control-sm" value={buyer.gstin} onChange={(e) => updateBuyer("gstin", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label ">State</label>
                                                        <input type="text" className="form-control form-control-sm" value={buyer.state} onChange={(e) => updateBuyer("state", e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* VENDOR Details (Supplier) */}
                                            <div className="col-md-6">
                                                <h6 className="d-flex align-items-center"><FiTruck className="me-2 text-info"/> Vendor Details (**Supplier**)</h6>
                                                <div className="row g-2">
                                                    <div className="col-12">
                                                        <label className="form-label ">Vendor Name</label>
                                                        <input type="text" className="form-control form-control-sm" value={vendor.name} onChange={(e) => updateVendor("name", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label ">Email</label>
                                                        <input type="email" className="form-control form-control-sm" value={vendor.email} onChange={(e) => updateVendor("email", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6"> 
                                                        <label className="form-label ">Phone</label>
                                                        <input type="text" className="form-control form-control-sm" value={vendor.phone} onChange={(e) => updateVendor("phone", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label ">GSTIN (Optional)</label>
                                                        <input type="text" className="form-control form-control-sm" value={vendor.gstin} onChange={(e) => updateVendor("gstin", e.target.value)} />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label className="form-label ">State</label>
                                                        <input type="text" className="form-control form-control-sm" value={vendor.state} onChange={(e) => updateVendor("state", e.target.value)} />
                                                    </div>
                                                    <div className="col-12">
                                                        <label className="form-label ">Address</label>
                                                        <input type="text" className="form-control form-control-sm" value={vendor.address} onChange={(e) => updateVendor("address", e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <hr/>


                                <h6 className="card-title p-2 mb-2 d-flex align-items-center"> PO Items <FiList className="ms-2 text-secondary"/> </h6>
                                {/* Items Table */}
                                <div className="card mb-4">
                                    <div className="card-body p-2">
                                        
                                        <div className="table-responsive">
                                            <table className="table table-sm table-hover mb-0">
                                                <thead >
                                                    <tr>
                                                        <th style={{ width: "35%" }}>Description</th>
                                                        <th style={{ width: "10%" }}>HSN/SAC</th>
                                                        <th style={{ width: "8%" }}>Qty</th>
                                                        <th style={{ width: "12%" }}>Rate ({gstInclusive ? 'Inc. Tax' : 'Excl. Tax'})</th>
                                                        {applyGSTItemWise && <th style={{ width: "10%" }}>Tax %</th>}
                                                        <th style={{ width: "15%" }}>Amount (Inc. Tax)</th>
                                                        <th style={{ width: "10%" }} className="text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.description}
                                                                    onChange={(e) => updateItem(index, "description", e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    className="form-control form-control-sm"
                                                                    value={item.hsn}
                                                                    onChange={(e) => updateItem(index, "hsn", e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={item.qty}
                                                                    onChange={(e) => updateItem(index, "qty", parseFloat(e.target.value) || 0)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    className="form-control form-control-sm"
                                                                    value={item.rate}
                                                                    onChange={(e) => updateItem(index, "rate", parseFloat(e.target.value) || 0)}
                                                                />
                                                            </td>
                                                            {applyGSTItemWise && (
                                                                <td>
                                                                    <select
                                                                        className="form-select form-select-sm"
                                                                        value={item.tax}
                                                                        onChange={(e) => updateItem(index, "tax", parseFloat(e.target.value))}
                                                                    >
                                                                        {GST_SLABS.map((g) => (
                                                                            <option key={g} value={g}>
                                                                                {g}%
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                            )}
                                                            <td>
                                                                <span className="form-control-plaintext form-control-sm fw-bold">
                                                                    {getItemAmountDisplay(item, applyGSTItemWise)}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">
                                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(index)}>
                                                                    <FiTrash2 />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="p-3">
                                            <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>
                                                <FiPlus /> Add Item
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-7">
                                        <h6 className="d-flex align-items-center"><FiFileText className="me-2 text-secondary"/> Notes/Terms</h6>
                                        <textarea 
                                            className="form-control form-control-sm" 
                                            rows="4" 
                                            value={purchaseOrder.notes} 
                                            onChange={(e) => updatePOMeta("notes", e.target.value)}
                                            placeholder="Enter delivery instructions, payment terms, or special conditions..."
                                        ></textarea>
                                    </div>
                                    <div className="col-md-5">
                                        {/* Purchase Order Summary Card */}
                                        <div className="card border-primary shadow-sm">
                                            <div className="card-header bg-primary text-white p-2">
                                                <h6 className="mb-0 d-flex align-items-center"><FaIndianRupeeSign className="me-2"/> PO Summary (Amount Payable)</h6>
                                            </div>
                                            <div className="card-body p-2">
                                                <table className="table table-sm mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <th>Item Subtotal (Excl/Inc Tax)</th>
                                                            <td className="text-end">₹ {itemSubtotal.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Discount ({purchaseOrder.discount}%)</th>
                                                            <td className="text-end text-danger">- ₹ {discountAmount.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>Additional Charges</th>
                                                            <td className="text-end">+ ₹ {Number(additionalCharges).toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="fw-bold">Taxable Amount</th>
                                                            <td className="text-end fw-bold">₹ {taxableAmount.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="text-success">Total GST ({isInterState ? 'IGST' : 'CGST + SGST'})</th>
                                                            <td className="text-end text-success">+ ₹ {totalTax.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>&nbsp;&nbsp;CGST:</th>
                                                            <td className="text-end">₹ {CGST.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>&nbsp;&nbsp;SGST:</th>
                                                            <td className="text-end">₹ {SGST.toFixed(2)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th>&nbsp;&nbsp;IGST:</th>
                                                            <td className="text-end">₹ {IGST.toFixed(2)}</td>
                                                        </tr>
                                                        <tr className="table-primary">
                                                            <th className="h5 mb-0">Grand Total</th>
                                                            <td className="text-end h5 mb-0">₹ {grandTotal.toFixed(2)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Submit Button */}
                                <div className="mt-4 text-center">
                                    <button type="submit" className="btn btn-lg btn-success d-flex align-items-center mx-auto">
                                        <FiCheckCircle className="me-2"/> Save Purchase Order
                                    </button>
                                </div>

                            </div>
                        </div>
                        
                        <div className="mb-5"></div>
                    </div>
                </form>
            </main>
        </>
    );
}

export default CreatePurchaseOrder;